// External libraries
import { RetryLink } from 'apollo-link-retry';
import type { RetryFunction } from 'apollo-link-retry/lib/retryFunction';
// @ts-expect-error untyped-import
import { strictIsFinite } from '@checkout/react-components';

// Internal modules
import { logger } from '@/lib/logger';
import { camelToSnakeCase } from '@/lib/utils';
import { getApiCorrelationId } from '../utils';
import { hasExactTreatment } from '@/lib/features/utils';

/**
 * Mutations and queries in this set are not allowed to be retried
 *
 * SignUpNewMember & CreateMemberAccount:
 *  Retries on these mutations can cause ACCOUNT_ALREADY_EXISTS
 *  errors if the OAS call already succeeded or if it is in-flight
 *  and the error is due to an OAS socket timeout.
 */
const RETRY_OPERATION_BLOCKLIST = new Set([
  'SignUpNewMemberMutation',
  'CreateMemberAccountMutation',
]);

const MAX_ATTEMPTS = 3;

const isOperationInBlocklist = ({
  operationName,
}: {
  operationName: string;
}) => {
  return RETRY_OPERATION_BLOCKLIST.has(operationName);
};

export const attempts: RetryFunction = (count, operation, error) => {
  // Certain operations are blocked from being retried
  const operationName = camelToSnakeCase(operation.operationName);
  const isOperationRetryBlocked = isOperationInBlocklist(operation);
  const { skipRetries = false, maxAttempts } = operation.getContext();
  const { statusCode } = error;

  const has400RangeStatusCode =
    typeof statusCode === 'number' && statusCode < 500;

  const isRetryDisabled = getIsRetryDisabled();

  if (
    isRetryDisabled ||
    isOperationRetryBlocked ||
    skipRetries ||
    has400RangeStatusCode
  ) {
    logger.info(`${operationName}_skip_retry`, {
      isOperationRetryBlocked,
      skipRetries,
      statusCode,
    });
    return false;
  }

  const max = strictIsFinite(maxAttempts) ? maxAttempts : MAX_ATTEMPTS;
  logger.info(`retry_${operationName}_after_attempt_${count}_failed`, {
    apiCorrelationId: getApiCorrelationId({ networkError: error }),
    attemptsRemaining: max - count,
    error: error.message,
  });

  return count < max;
};

export const retryLink = new RetryLink({
  attempts,
});

function getIsRetryDisabled() {
  return hasExactTreatment({
    feature: 'weasley_request_retry_enabled',
    treatment: 'Trmt_weasley_request_retry_disabled',
  });
}
